import { defineStore, acceptHMRUpdate } from "pinia";
import { ref, computed, watch } from "vue";
import { useTransition } from "@vueuse/core";
import { interpolateNumber } from "d3-interpolate";
import { useAudioAnalyser, useAudioElement } from "../composables";
import { useAuth, useRAF, useSpotify } from ".";
import { useRouter } from "../router/sage-router";

import {
  AudioSource,
  RadioParadiseStation,
  enumKeyToLabel,
  type RadioParadiseStation as RadioParadiseStationType,
} from "@wearesage/shared";
import { clamp, easeInOut } from "../util";
import { MediaSessionManager } from "../classes/MediaSessionManager";
import { useRadioStream } from "../composables/useRadioStream";

export const AudioSourceIcons: Record<AudioSource, string> = {
  [AudioSource.SPOTIFY]: "spotify",
  [AudioSource.AUDIUS]: "audius",
  [AudioSource.MICROPHONE]: "microphone",
  [AudioSource.RADIO_PARADISE]: "radio-paradise",
  [AudioSource.KEXP]: "kexp",
  [AudioSource.FILE]: "upload",
  [AudioSource.BROWSER_AUDIO]: "sound",
  [AudioSource.NONE]: "sound",
};

export const SelectableAudioSources: AudioSource[] = [
  AudioSource.SPOTIFY,
  AudioSource.AUDIUS,
  AudioSource.MICROPHONE,
  AudioSource.RADIO_PARADISE,
  AudioSource.KEXP,
  AudioSource.FILE,
];

export function getAudioSourceLabel(source: AudioSource): string {
  const enumKey = Object.keys(AudioSource).find((key) => AudioSource[key as keyof typeof AudioSource] === source);
  return enumKey ? enumKeyToLabel(enumKey) : "Unknown";
}

export const useSources = defineStore("sources", () => {
  const router = useRouter();
  const spotify = useSpotify();
  const raf = useRAF();
  const mediaSessionManager = new MediaSessionManager();

  let audioPlaying = ref(false);
  let audioStoreVolume = ref(0);
  let audioStoreStream = ref(0);

  const connectToAudioStore = async () => {
    try {
      const { useAudio } = await import("./audio");
      const audio = useAudio();

      watch(
        () => audio.playing,
        (playing) => {
          audioPlaying.value = playing;
        },
        { immediate: true }
      );

      watch(
        () => audio.volume,
        (volume) => {
          audioStoreVolume.value = volume;
        },
        { immediate: true }
      );

      watch(
        () => audio.stream,
        (stream) => {
          audioStoreStream.value = stream;
        },
        { immediate: true }
      );

      console.log(" Sources: Connected to audio store for volume/stream/playing data");
    } catch (error) {
      console.warn("Sources: Could not connect to audio store:", error);
    }
  };

  connectToAudioStore();
  const source = ref<AudioSource | null>(null);
  const radioParadiseStation = ref<RadioParadiseStationType>(RadioParadiseStation.MAIN_MIX);

  const radioStream = computed(() => {
    if (source.value === AudioSource.RADIO_PARADISE || source.value === AudioSource.KEXP) {
      const preferLossless = false;
      return useRadioStream(source.value, radioParadiseStation.value, preferLossless);
    }
    return null;
  });

  const prettySource = computed(() => {
    if (source.value !== null) {
      if (radioStream.value?.stationName.value) {
        return radioStream.value.stationName.value;
      }
      return getAudioSourceLabel(source.value);
    }
    return null;
  });
  const sourceIcon = computed(() => (source.value !== null ? AudioSourceIcons[source.value] : "sound"));
  const useSpotifyValues = computed(() => source.value === AudioSource.SPOTIFY && spotify.playing);

  const spotifyVolume = computed(() => spotify.volume);
  const microphoneVolume = computed(() => {
    return source.value === AudioSource.MICROPHONE ? audioStoreVolume.value : 0;
  });

  const rawSourceVolume = computed(() => {
    if (useSpotifyValues.value) return spotifyVolume.value;
    return audioStoreVolume.value;
  });

  const interpolatedVolume = ref(0);
  const isVolumeInterpolating = ref(false);

  interpolatedVolume.value = rawSourceVolume.value;

  watch(
    () => rawSourceVolume.value,
    (newVolume) => {
      if (!isVolumeInterpolating.value) {
        interpolatedVolume.value = newVolume;
      }
    }
  );

  const isPlayingAThing = computed(() => {
    if (source.value === AudioSource.SPOTIFY) return spotify.playing;
    if (source.value === AudioSource.MICROPHONE) return true;
    return audioPlaying.value;
  });

  const minVolume = useTransition(
    computed(() => (isPlayingAThing.value ? 0 : 1)),
    {
      duration: 1000,
      transition: easeInOut,
    }
  );

  const lastSourceStream = ref(0);
  const streamOffset = ref(0);
  const hasCalculatedOffset = ref(false);

  const rawStream = computed(() => (useSpotifyValues.value ? spotify.stream : audioStoreStream.value));

  const isNewStreamStable = computed(() => {
    return rawStream.value > 0.001 && source.value !== null && source.value !== AudioSource.MICROPHONE;
  });

  watch(
    () => isNewStreamStable.value,
    (stable) => {
      if (stable && !hasCalculatedOffset.value) {
        const newOffset = lastSourceStream.value - rawStream.value;
        streamOffset.value = newOffset;
        hasCalculatedOffset.value = true;
        console.log(` Stream offset calculated once: ${newOffset.toFixed(3)}`);
      }
    }
  );

  watch(
    () => source.value,
    (newSource, oldSource) => {
      if (oldSource !== null && newSource !== oldSource) {
        console.log(` Stream continuity: switching from ${getAudioSourceLabel(oldSource)} to ${getAudioSourceLabel(newSource)}`);

        hasCalculatedOffset.value = false;

        const fromVolume = interpolatedVolume.value || rawSourceVolume.value;
        const toVolume = rawSourceVolume.value;

        if (Math.abs(fromVolume - toVolume) > 0.01) {
          console.log(` Volume interpolation: ${fromVolume.toFixed(3)} → ${toVolume.toFixed(3)}`);

          isVolumeInterpolating.value = true;
          const volumeInterpolator = interpolateNumber(fromVolume, toVolume);

          raf.remove("volume-source-transition");
          raf.add(
            (now, progress) => {
              interpolatedVolume.value = volumeInterpolator(progress);
              if (progress >= 1) {
                isVolumeInterpolating.value = false;
              }
            },
            {
              id: "volume-source-transition",
              duration: 750,
            }
          );
        } else {
          interpolatedVolume.value = toVolume;
          isVolumeInterpolating.value = false;
        }
      }
    }
  );

  const volume = computed(() => clamp(interpolatedVolume.value, minVolume.value, 1));
  const stream = computed(() => {
    if (source.value === AudioSource.MICROPHONE) {
      return rawStream.value;
    }
    return rawStream.value + streamOffset.value;
  });

  watch(
    () => stream.value,
    (newStream) => {
      if (isNewStreamStable.value) {
        lastSourceStream.value = newStream;
      }
    }
  );
  const playing = computed(() => isPlayingAThing.value);

  const isTransitioning = ref(false);
  const isQueueLocked = ref(false);

  async function selectSource(newSource: AudioSource) {
    if (isTransitioning.value) {
      console.warn(` Source transition already in progress, ignoring switch to ${getAudioSourceLabel(newSource)}`);
      return;
    }
    if (!SelectableAudioSources.includes(newSource)) {
      console.warn(`Invalid audio source: ${newSource}`);
      return;
    }

    isTransitioning.value = true;
    isQueueLocked.value = true;

    try {
      const oldSource = source.value;
      if (oldSource !== newSource) {
        console.log(` Switching from ${getAudioSourceLabel(oldSource)} to ${getAudioSourceLabel(newSource)}`);
        await performSourceCleanup(oldSource);
      }

      mediaSessionManager.activateForSource(newSource);
      source.value = newSource;

      if (newSource !== AudioSource.SPOTIFY && newSource !== AudioSource.NONE) {
        console.log(` Selected audio source: ${getAudioSourceLabel(newSource)}`);
      }

      if (newSource === AudioSource.SPOTIFY) {
        if (spotify.accessToken) {
          // Token exists, so we can proceed with initialization
          // which will fetch the current playing state.
          console.log("Spotify token found, initializing player.");
          spotify.initialize();
        } else {
          // No token, we need to authenticate.
          // This will redirect the user.
          console.log("No Spotify token, redirecting to auth.");
          spotify.getSpotifyTokens();
          // Bail out of the source selection, as we are redirecting.
          isTransitioning.value = false;
          isQueueLocked.value = false;
          return;
        }
      }

      if (source.value === AudioSource.AUDIUS) {
        router.push("/audius");
      }

      if (newSource === AudioSource.RADIO_PARADISE || newSource === AudioSource.KEXP) {
        await handleRadioSourceSelection(newSource);
      }

      if (newSource === AudioSource.MICROPHONE) {
        await handleMicrophoneSourceSelection();
      }

      if (newSource === AudioSource.FILE) {
        await handleFileSourceSelection();
      }

      try {
        const { useQueue } = await import("./queue");
        const queue = useQueue();
        console.log(` Post-transition queue verification: ${queue.queue.tracks.length} tracks, playing: ${queue.queue.isPlaying}`);
      } catch (error) {
        console.warn("Could not verify queue state after transition:", error);
      }

      console.log(`✅ Source transition completed: ${getAudioSourceLabel(newSource)}`);
    } catch (error) {
      console.error(`❌ Source transition failed to ${getAudioSourceLabel(newSource)}:`, error);
      source.value = AudioSource.NONE;
      mediaSessionManager.deactivate();
    } finally {
      isTransitioning.value = false;
      isQueueLocked.value = false;
    }
  }

  async function handleRadioSourceSelection(radioSource: AudioSource) {
    const stream = radioStream.value;
    if (!stream?.streamUrl.value) {
      console.warn(`No stream URL available for ${getAudioSourceLabel(radioSource)}`);
      return;
    }

    try {
      const { useAudioAnalyser } = await import("../composables/audio/useAudioAnalyser");
      const audioAnalyser = useAudioAnalyser();
      await audioAnalyser.audioSystem.initializeAudioElement();

      const { useAudio } = await import("./audio");
      const audio = useAudio();

      console.log(` Setting stream URL: ${stream.streamUrl.value}`);
      console.log(` Stream quality: ${stream.streamQuality.value}`);

      await audio.setSource(stream.streamUrl.value);

      if (radioSource === AudioSource.RADIO_PARADISE) {
        await joinRadioParadiseSpace(radioParadiseStation.value);
      }
    } catch (error) {
      console.error(`Failed to initialize radio source ${getAudioSourceLabel(radioSource)}:`, error);
    }
  }

  async function handleMicrophoneSourceSelection() {
    try {
      console.log(" Initializing microphone source...");

      const { useAudioAnalyser } = await import("../composables/audio/useAudioAnalyser");
      const audioAnalyser = useAudioAnalyser();
      const success = await audioAnalyser.initializeMicrophone();

      if (success) {
        console.log(" Microphone initialized successfully!");
      } else {
        console.error(" Failed to initialize microphone");
        source.value = AudioSource.NONE;
      }
    } catch (error) {
      console.error(" Error initializing microphone:", error);
      source.value = AudioSource.NONE;
    }
  }

  async function handleFileSourceSelection() {
    try {
      console.log(" Initializing file source...");
      const { useAudioAnalyser } = await import("../composables/audio/useAudioAnalyser");
      const audioAnalyser = useAudioAnalyser();
      await audioAnalyser.audioSystem.initializeAudioElement();
      console.log(" File source initialized - ready for file selection");
    } catch (error) {
      console.error(" Error initializing file source:", error);
      source.value = AudioSource.NONE;
    }
  }

  async function selectRadioParadiseStation(station: RadioParadiseStationType) {
    radioParadiseStation.value = station;
    localStorage.setItem("radioParadiseStation", station.toString());

    console.log(` Selected Radio Paradise station: ${radioStream.value?.stationName.value}`);

    if (source.value === AudioSource.RADIO_PARADISE) {
      await handleRadioSourceSelection(AudioSource.RADIO_PARADISE);
    }
  }

  async function performSourceCleanup(oldSource: AudioSource | null) {
    if (!oldSource) return;

    console.log(` Cleaning up ${getAudioSourceLabel(oldSource)} source`);
    mediaSessionManager.deactivate();

    try {
      const { useAudio } = await import("./audio");
      const audio = useAudio();

      if (audio.playing) {
        console.log(" Stopping audio playback for source switch");
        audio.pause();
      }

      const { useQueue } = await import("./queue");
      const queue = useQueue();

      console.log(
        ` Aggressively resetting queue for source switch (current tracks: ${queue.queue.tracks.length}, playing: ${queue.queue.isPlaying})`
      );
      queue.reset();

      await new Promise((resolve) => setTimeout(resolve, 50));

      if (queue.queue.tracks.length > 0) {
        console.warn(` Queue still has ${queue.queue.tracks.length} tracks after reset, force clearing...`);
        queue.reset();
      }

      switch (oldSource) {
        case AudioSource.SPOTIFY:
          const { useSpotify } = await import("./spotify");
          const spotify = useSpotify();
          spotify.reset();
          console.log(" Spotify store reset and cleanup complete");
          break;
        case AudioSource.AUDIUS:
          console.log(" Audius cleanup complete");
          break;
        case AudioSource.RADIO_PARADISE:
          await leaveRadioParadiseSpace();
          console.log(" Radio Paradise cleanup complete");
          break;
        case AudioSource.MICROPHONE:
          console.log(" Microphone cleanup complete");
          break;
        case AudioSource.FILE:
          await cleanupFileSource();
          console.log(" File cleanup complete");
          break;
        default:
          console.log(` Generic cleanup for ${getAudioSourceLabel(oldSource)}`);
          break;
      }
    } catch (error) {
      console.warn("Error during source cleanup:", error);
    }
  }

  const currentFileUrls = ref<string[]>([]);

  async function cleanupFileSource() {
    try {
      currentFileUrls.value.forEach((url) => {
        URL.revokeObjectURL(url);
        console.log(" Revoked object URL:", url);
      });
      currentFileUrls.value = [];
    } catch (error) {
      console.warn("Error during file cleanup:", error);
    }
  }

  async function loadFiles(files: FileList) {
    if (!files || files.length === 0) {
      console.warn(" No files provided to load");
      return;
    }
    if (source.value !== AudioSource.FILE) {
      console.warn(" Cannot load files - not in file source mode");
      return;
    }
    if (isQueueLocked.value) {
      console.log(" Queue locked during source transition, skipping file load");
      return;
    }

    try {
      console.log(` Loading ${files.length} file(s)...`);
      await cleanupFileSource();

      const { useQueue } = await import("./queue");
      const queue = useQueue();
      const { useAudio } = await import("./audio");
      const audio = useAudio();
      const tracks = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith("audio/")) {
          console.warn(` Skipping non-audio file: ${file.name}`);
          continue;
        }

        console.log(` Processing file: ${file.name} (${file.type})`);
        const objectUrl = URL.createObjectURL(file);
        currentFileUrls.value.push(objectUrl);
        const metadata = await extractFileMetadata(file);

        const track = {
          id: `file:${file.name}:${file.lastModified}`,
          source: AudioSource.FILE,
          sourceId: objectUrl,
          title: metadata.title || file.name.replace(/\.[^/.]+$/, ""),
          artist: metadata.artist || "Unknown Artist",
          album: metadata.album,
          duration: metadata.duration,
          artwork: metadata.artwork ? { large: metadata.artwork } : undefined,
          artworkBlobUrls: metadata.artworkBlobUrls,
          rawData: { file, objectUrl },
        };

        tracks.push(track);
        console.log(` Created track: ${track.title} by ${track.artist}`);
      }

      if (tracks.length > 0) {
        queue.setQueue(tracks, 0);
        const firstTrack = tracks[0];
        await audio.setSource(firstTrack.sourceId);
        if (!queue.queue.isPlaying) {
          queue.togglePlayPause();
        }
        console.log(` Loaded ${tracks.length} file(s) into queue, playing: ${firstTrack.title}`);
      } else {
        console.warn(" No valid audio files found to load");
      }
    } catch (error) {
      console.error(" Error loading files:", error);
      await cleanupFileSource();
    }
  }

  function setSourceFromServer(serverSource: AudioSource) {
    if (SelectableAudioSources.includes(serverSource)) {
      console.log(" Auto-selecting audio source from server:", getAudioSourceLabel(serverSource));
      selectSource(serverSource);
    }
  }

  function initializeSource() {
    const savedStation = localStorage.getItem("radioParadiseStation");
    if (savedStation) {
      const stationValue = parseInt(savedStation) as RadioParadiseStationType;
      if (Object.values(RadioParadiseStation).includes(stationValue)) {
        radioParadiseStation.value = stationValue;
        console.log(" Restored Radio Paradise station preference:", stationValue);
      }
    }
    console.log(" No auto-source restoration - forcing fresh music discovery! ");
    source.value = null;
  }

  // The spotify store now initializes itself and reads from localStorage.
  // We just need to check if it found a token.
  if (spotify.accessToken) {
    selectSource(AudioSource.SPOTIFY);
  } else {
    initializeSource();
  }

  async function reset() {
    console.log(" Resetting sources store state");
    await performSourceCleanup(source.value);
    raf.remove("volume-source-transition");
    isVolumeInterpolating.value = false;
    source.value = null;
    localStorage.removeItem("selectedAudioSource");

    try {
      const { useQueue } = await import("./queue");
      const queue = useQueue();
      queue.reset();
    } catch (error) {
      console.warn("Could not reset queue during sources cleanup:", error);
    }
  }

  return {
    source,
    radioParadiseStation,
    radioStream,
    prettySource,
    sourceIcon,
    selectSource,
    selectRadioParadiseStation,
    setSourceFromServer,
    loadFiles,
    volume,
    stream,
    playing,
    reset,
    mediaSessionManager,
    isTransitioning,
    isQueueLocked,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSources, import.meta.hot));
}
